import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, BadgeCheck, Calendar, CreditCard, FileCheck2, Fuel, Gauge, IndianRupee, MapPin, MessageCircle, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import BikeCard from "@/components/site/BikeCard";
import WhatsAppFloat from "@/components/site/WhatsAppFloat";
import { MaskedLine, Reveal } from "@/components/site/Reveal";
import { Input } from "@/components/ui/input";
import { api, bikeImage, formatINR, formatKm, formatApiErrorDetail } from "@/api";
import { waLink } from "@/config";

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

const BookingBox = ({ bike }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [config, setConfig] = useState({ enabled: false, amount_paise: 500000 });
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    api.get("/bookings/config").then(({ data }) => setConfig(data)).catch(() => {});
  }, []);

  const handlePay = async () => {
    if (!name.trim() || !phone.trim()) {
      toast.error("Apna naam aur phone number daalein");
      return;
    }
    if (!config.enabled) {
      toast.info("Online payment jald aa rahi hai — abhi WhatsApp par book karein");
      window.open(waLink(`Hi Deal 2 Wheel! I want to book the ${bike.title} (${formatINR(bike.price)}). Booking amount ₹5,000 ke liye ready hoon.`), "_blank");
      return;
    }
    setPaying(true);
    try {
      const { data } = await api.post("/bookings/create-order", { bike_id: bike.id, name, phone });
      const loaded = await loadRazorpayScript();
      if (!loaded) throw new Error("checkout");
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount_paise,
        currency: "INR",
        name: "Deal 2 Wheel",
        description: `Booking — ${bike.title}`,
        order_id: data.order_id,
        prefill: { name, contact: phone },
        theme: { color: "#E1FF00" },
        handler: async (res) => {
          try {
            await api.post("/bookings/verify", { booking_id: data.booking_id, ...res });
            toast.success("Booking confirmed! Deal 2 Wheel aapse jald contact karega.");
            setOpen(false);
          } catch {
            toast.error("Payment verify nahi hua — WhatsApp par contact karein");
          }
        },
      });
      rzp.open();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setPaying(false);
    }
  };

  if (bike.status === "sold") return null;

  return (
    <div className="mt-9 border border-volt/30 bg-volt/5 p-6" data-testid="booking-box">
      <h3 className="font-display font-semibold uppercase tracking-tight text-lg flex items-center gap-2">
        <CreditCard className="w-5 h-5 text-volt" /> Buy Now — Reserve This Bike
      </h3>
      <p className="mt-2 text-white/55 text-sm leading-relaxed">
        Sirf ₹5,000 booking amount se bike reserve karein. Baaki payment showroom par — test ride ke baad.
      </p>
      {!open ? (
        <div className="mt-5 flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => setOpen(true)}
            data-testid="booking-pay-open-button"
            className="flex-1 inline-flex items-center justify-center gap-2 bg-volt text-black font-semibold px-6 py-3.5 rounded-full hover:scale-[1.03] volt-glow transition-transform duration-300 whitespace-nowrap"
          >
            <CreditCard className="w-4 h-4" /> Pay ₹5,000 & Book
          </button>
          <a
            href={waLink(`Hi Deal 2 Wheel! I want to book the ${bike.title} (${bike.year}) at ${formatINR(bike.price)}. Please reserve it for me.`)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="booking-whatsapp-button"
            className="inline-flex items-center justify-center gap-2 border border-white/25 text-white font-semibold px-6 py-3.5 rounded-full hover:border-volt hover:text-volt transition-colors duration-300"
          >
            <MessageCircle className="w-4 h-4" /> Book on WhatsApp
          </a>
        </div>
      ) : (
        <div className="mt-5 space-y-4" data-testid="booking-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Aapka naam"
              data-testid="booking-name-input"
              className="bg-[#0a0a0a] border-white/15 text-white rounded-none h-12"
            />
            <Input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Phone number"
              data-testid="booking-phone-input"
              className="bg-[#0a0a0a] border-white/15 text-white rounded-none h-12"
            />
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handlePay}
              disabled={paying}
              data-testid="booking-pay-button"
              className="flex-1 inline-flex items-center justify-center gap-2 bg-volt text-black font-semibold px-6 py-3.5 rounded-full hover:brightness-110 disabled:opacity-50 transition-[filter] duration-300"
            >
              {paying ? "Processing..." : "Proceed to Pay ₹5,000"}
            </button>
            <button
              onClick={() => setOpen(false)}
              data-testid="booking-cancel-button"
              className="text-white/50 text-sm hover:text-white transition-colors duration-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const BikeDetail = () => {
  const { id } = useParams();
  const [bike, setBike] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/bikes/${id}`)
      .then(({ data }) => {
        setBike(data);
        return api.get("/bikes");
      })
      .then(({ data }) => {
        setSimilar(data.filter((b) => b.id !== id && b.status !== "sold").slice(0, 3));
      })
      .catch(() => setBike(null))
      .finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [id]);

  if (loading) {
    return (
      <div className="bg-[#050505] min-h-screen">
        <Nav />
        <div className="max-w-7xl mx-auto px-5 md:px-8 pt-40 pb-24 grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div className="aspect-[4/3] bg-card border border-white/10 animate-pulse" />
          <div className="space-y-4"><div className="h-10 bg-card animate-pulse w-3/4" /><div className="h-6 bg-card animate-pulse w-1/2" /><div className="h-40 bg-card animate-pulse" /></div>
        </div>
      </div>
    );
  }

  if (!bike) {
    return (
      <div className="bg-[#050505] min-h-screen text-white">
        <Nav />
        <div className="max-w-7xl mx-auto px-5 md:px-8 pt-48 pb-32 text-center" data-testid="bike-not-found">
          <h1 className="font-display font-semibold uppercase tracking-tighter text-5xl">Bike nahi mili</h1>
          <p className="mt-4 text-white/50">Ho sakta hai ye bike bik gayi ho. Fresh stock dekho.</p>
          <Link to="/inventory" data-testid="bike-not-found-back" className="mt-8 inline-flex items-center gap-2 bg-volt text-black font-semibold px-7 py-3.5 rounded-full">
            <ArrowLeft className="w-4 h-4" /> Back to Inventory
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const specs = [
    { icon: Calendar, label: "Year", value: bike.year },
    { icon: Gauge, label: "Driven", value: formatKm(bike.km) },
    { icon: Fuel, label: "Fuel", value: bike.fuel },
    { icon: MapPin, label: "Location", value: bike.location || "Deal 2 Wheel Showroom" },
  ];

  const includes = [
    { icon: BadgeCheck, text: "120-point inspection passed" },
    { icon: FileCheck2, text: "RC transfer & paperwork handled" },
    { icon: ShieldCheck, text: "Transparent service history" },
  ];

  return (
    <div className="bg-[#050505] text-white min-h-screen" data-testid="bike-detail-page">
      <Nav />
      <WhatsAppFloat />

      <section className="max-w-7xl mx-auto px-5 md:px-8 pt-28 md:pt-36 pb-16">
        <Link to="/inventory" data-testid="bike-detail-back" className="inline-flex items-center gap-2 text-white/50 hover:text-volt text-sm uppercase tracking-widest transition-colors duration-300 mb-8">
          <ArrowLeft className="w-4 h-4" /> All Bikes
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-3 relative overflow-hidden border border-white/10 bg-[#0a0a0a]"
          >
            <img src={bikeImage(bike.image)} alt={bike.title} className="w-full h-auto object-contain" data-testid="bike-detail-image" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
            {bike.status === "sold" && (
              <span className="absolute top-5 left-5 bg-destructive text-white text-sm font-bold uppercase tracking-widest px-4 py-2" data-testid="bike-detail-sold-badge">Sold</span>
            )}
          </motion.div>

          <div className="lg:col-span-2">
            <p className="text-volt uppercase tracking-[0.3em] text-xs font-semibold mb-3">{bike.brand} · {bike.category}</p>
            <h1 className="font-display font-semibold uppercase tracking-tighter leading-[0.95] text-4xl md:text-5xl" data-testid="bike-detail-title">
              <MaskedLine delay={0.1}>{bike.title}</MaskedLine>
            </h1>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="mt-4 font-display font-semibold text-4xl text-volt flex items-center"
              data-testid="bike-detail-price"
            >
              {formatINR(bike.price)}
            </motion.p>

            <div className="grid grid-cols-2 gap-3 mt-7" data-testid="bike-detail-specs">
              {specs.map((s) => (
                <div key={s.label} className="border border-white/10 bg-card p-4">
                  <s.icon className="w-4 h-4 text-volt mb-2" />
                  <p className="text-[10px] uppercase tracking-[0.2em] text-white/40">{s.label}</p>
                  <p className="font-semibold text-sm mt-0.5">{s.value}</p>
                </div>
              ))}
            </div>

            {bike.description && (
              <p className="mt-7 text-white/60 text-sm leading-relaxed border-l-2 border-volt pl-4" data-testid="bike-detail-description">
                {bike.description}
              </p>
            )}

            <div className="mt-7 space-y-3">
              {includes.map((inc) => (
                <div key={inc.text} className="flex items-center gap-3 text-sm text-white/70">
                  <inc.icon className="w-4 h-4 text-volt shrink-0" />
                  {inc.text}
                </div>
              ))}
            </div>

            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <a
                href={waLink(`Hi Deal 2 Wheel! I'm interested in the ${bike.title} (${bike.year}, ${formatKm(bike.km)}) listed at ${formatINR(bike.price)}. Can I book a test ride?`)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="bike-detail-whatsapp-cta"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-volt text-black font-semibold px-6 py-4 rounded-full hover:scale-[1.03] volt-glow transition-transform duration-300"
              >
                <MessageCircle className="w-5 h-5" /> Enquire on WhatsApp
              </a>
              <a
                href={waLink(`Hi Deal 2 Wheel! I want to exchange my old bike for the ${bike.title}.`)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="bike-detail-exchange-cta"
                className="inline-flex items-center justify-center gap-2 border border-white/25 text-white font-semibold px-6 py-4 rounded-full hover:border-volt hover:text-volt transition-colors duration-300"
              >
                <IndianRupee className="w-5 h-5" /> Exchange Offer
              </a>
            </div>

            <BookingBox bike={bike} />
          </div>
        </div>
      </section>

      {similar.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 md:px-8 pb-24" data-testid="similar-bikes-section">
          <Reveal>
            <h2 className="font-display font-semibold uppercase tracking-tighter text-3xl md:text-4xl mb-10">
              Similar <span className="text-outline">Machines</span>
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {similar.map((b, i) => (
              <BikeCard key={b.id} bike={b} index={i} />
            ))}
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default BikeDetail;
